/**
 * WorkspaceNav — the Workspace-INTERNAL left panel (Workspace v2).
 *
 * Replaces the page's horizontal tab strip with a gentle two-group left rail,
 * returning to the prototype's sidebar intent. Two groups:
 *   • PROCESSES — the boards a case moves through start-to-finish (Intake
 *     Pipeline, All tasks). Each carries a small unit-less identity dot.
 *   • VIEWS — cross-board personal lenses (My Work, Caseload, Completed).
 *
 * This is Workspace-internal ONLY — the app shell's top nav (AppShell) is
 * untouched; the older, less-technical teams keep the familiar website layout.
 *
 * Mirrors the shipped Admin console rail (AdminReviewTabs, X.3.7): a
 * `hidden md:flex` vertical rail plus a native <select> mobile fallback
 * (WorkspaceNavSelect) built from the SAME groups, so the panel is never
 * stranded off-screen on a phone. Keeps the existing `?view=` keys
 * (pipeline/mywork/tasks/caseload/completed) so deep-links + the page's
 * per-view query gating are unchanged — this is a chrome swap, not a data
 * change.
 *
 * Multi-board creation is a future slice; the "+ New process" affordance
 * renders DISABLED ("Soon") so the grouped structure visibly scales without
 * shipping a non-working feature.
 *
 * Gentle by design (nurses / social workers / OTs): plain labels, generous
 * padding, big calm targets, the brand palette — no jargon ("boards"/"kanban"),
 * no command palette.
 */

import { Fragment, type ReactNode } from 'react';
import type { WorkProcessWire } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';

// 'conversion' left the Workspace views 2026-07-14 (founder move ruling): the
// Conversion report now lives on the Reports page (?tab=conversion). The old
// ?view=conversion deep-link 302-redirects there (Workspace.tsx).
export type WorkspaceView = 'pipeline' | 'mytasks' | 'caseload' | 'completed';

/** Wayfinding identity dots for the process switcher — brand tokens, NO state
 *  meaning (register law §2). The intake board keeps sage-400 (today's dot);
 *  additional boards cycle the rest. */
const PROCESS_DOTS = ['bg-sage-400', 'bg-warm-400', 'bg-sage-800', 'bg-warm-700', 'bg-sage-300'];

interface NavItem {
  key: WorkspaceView;
  label: string;
  /** Processes only: a small identity dot, a brand token (no hardcoded hex). */
  dotClass?: string;
}

interface NavGroup {
  /** undefined → a headerless group: its items render with no section label
   *  (the leading "My Tasks" item sits above the first labelled group). */
  label?: string;
  items: ReadonlyArray<NavItem>;
}

/**
 * The grouped nav structure — the single source of truth for both the rail and
 * the mobile select. Processes hold the boards; Views hold the cross-board
 * lenses. Built to scale to more processes (a future slice); v1 ships the one
 * board. ("All tasks" was retired in Workspace v3; "My Tasks" — the unified
 * owner+collaborator agenda (HubMyTasks) — returns here as a View, relocated off
 * the home page so the hub reads as a launcher again. The home keeps a compact
 * glance, HubMyTasksRail.)
 */
export const WORKSPACE_NAV_GROUPS: ReadonlyArray<NavGroup> = [
  // My Tasks leads the rail (Amanda 2026-06-29) — the primary daily entry, a
  // headerless standalone item above the grouped boards/lenses. It stays in
  // WORKSPACE_VIEWS via the flatMap below, so the ?view=mytasks dispatch is
  // unchanged.
  {
    items: [
      { key: 'mytasks', label: 'My Tasks' },
    ],
  },
  {
    label: 'Processes',
    items: [
      { key: 'pipeline', label: 'Intake Pipeline', dotClass: 'bg-sage-400' },
    ],
  },
  {
    label: 'Views',
    items: [
      { key: 'caseload', label: 'Caseload' },
      { key: 'completed', label: 'Completed' },
    ],
  },
];

/** Every valid `?view=` key, in nav order — drives the page's param parse so
 *  the nav and the URL contract never drift. */
export const WORKSPACE_VIEWS: ReadonlyArray<WorkspaceView> = WORKSPACE_NAV_GROUPS.flatMap((g) =>
  g.items.map((i) => i.key),
);

export interface WorkspaceNavProps {
  view: WorkspaceView;
  onSelect: (next: WorkspaceView) => void;
  /** editable-workspace-processes (PR3): the process switcher. When provided
   *  (>0), the Processes group renders one tab per board (intake first). When
   *  absent/empty the group falls back to the single static "Intake Pipeline"
   *  item — byte-identical to the pre-PR3 single-process rail. */
  processes?: ReadonlyArray<WorkProcessWire>;
  selectedProcessId?: string | null;
  onSelectProcess?: (processId: string) => void;
  /** Gates the "+ New process" affordance (canManageWorkspaceProcesses). */
  canManageProcesses?: boolean;
  onNewProcess?: () => void;
}

/** Intake (is_system) first, then by sort_order — the switcher order. */
function orderProcesses(processes: ReadonlyArray<WorkProcessWire>): WorkProcessWire[] {
  return [...processes].sort((a, b) => (a.is_system === b.is_system ? a.sort_order - b.sort_order : a.is_system ? -1 : 1));
}

/**
 * The desktop rail — a soft sand panel of grouped, pill-shaped items. Hidden
 * below `md` (WorkspaceNavSelect replaces it there). Each group is its own
 * tablist; the active item is a soft sage pill.
 */
export function WorkspaceNav({ view, onSelect, processes, selectedProcessId, onSelectProcess, canManageProcesses, onNewProcess }: WorkspaceNavProps): ReactNode {
  const dynamicProcesses = processes && processes.length > 0 ? orderProcesses(processes) : null;
  return (
    <nav
      aria-label="Workspace sections"
      data-testid="workspace-nav"
      className="hidden md:flex flex-col bg-sand rounded-2xl p-3 md:w-52 md:shrink-0"
    >
      {WORKSPACE_NAV_GROUPS.map((group, gi) => (
        <div
          key={group.label ?? group.items[0]!.key}
          className={gi > 0 ? 'mt-4 pt-4 border-t border-sage-800/[7%]' : ''}
        >
          {group.label && (
            <p className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-sage-400">
              {group.label}
            </p>
          )}
          <div role="tablist" aria-label={group.label ?? group.items[0]!.label} className="flex flex-col gap-0.5">
            {/* Processes group with a live process list → dynamic board tabs
                (the switcher). Every other group → the static view items. */}
            {group.label === 'Processes' && dynamicProcesses ? (
              dynamicProcesses.map((proc, i) => {
                const isActive = view === 'pipeline' && (selectedProcessId ?? dynamicProcesses[0]!.id) === proc.id;
                // Back-compat: the intake board keeps the workspace-tab-pipeline id.
                const testId = proc.is_system ? 'workspace-tab-pipeline' : `workspace-process-tab-${proc.key}`;
                return (
                  <button
                    key={proc.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="workspace-tabpanel"
                    data-testid={testId}
                    onClick={() => onSelectProcess?.(proc.id)}
                    className={
                      'min-h-11 w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left rounded-large transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                      (isActive ? 'bg-sage-100 text-sage-800 font-semibold' : 'text-ink-soft hover:bg-sage-50 hover:text-sage-800')
                    }
                  >
                    <span aria-hidden="true" className={'w-2 h-2 rounded-pill shrink-0 ' + PROCESS_DOTS[i % PROCESS_DOTS.length]} />
                    <span className="flex-1 min-w-0 truncate">{proc.label}</span>
                  </button>
                );
              })
            ) : (
              group.items.map((item) => {
                const isActive = item.key === view;
                return (
                  <button
                    key={item.key}
                    id={`workspace-tab-${item.key}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="workspace-tabpanel"
                    data-testid={`workspace-tab-${item.key}`}
                    onClick={() => onSelect(item.key)}
                    className={
                      'min-h-11 w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left rounded-large transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                      (isActive
                        ? 'bg-sage-100 text-sage-800 font-semibold'
                        : 'text-ink-soft hover:bg-sage-50 hover:text-sage-800')
                    }
                  >
                    {item.dotClass && (
                      <span
                        aria-hidden="true"
                        className={'w-2 h-2 rounded-pill shrink-0 ' + item.dotClass}
                      />
                    )}
                    <span className="flex-1 min-w-0">{item.label}</span>
                  </button>
                );
              })
            )}
            {/* Processes group only: create a new board (capability-gated). */}
            {group.label === 'Processes' && canManageProcesses && onNewProcess && (
              <button
                type="button"
                data-testid="workspace-new-process"
                onClick={onNewProcess}
                className="min-h-11 w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left rounded-large text-sage-400 hover:bg-sage-50 hover:text-sage-800 transition-colors duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                <span aria-hidden="true" className="w-2 flex justify-center shrink-0 text-[15px] leading-none">+</span>
                <span className="flex-1 min-w-0">New process</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </nav>
  );
}

export interface WorkspaceNavSelectProps {
  view: WorkspaceView;
  onSelect: (next: WorkspaceView) => void;
  /** Caller supplies the responsive visibility (e.g. "w-full md:hidden"). */
  className?: string;
  /** PR3 process switcher (mobile) — mirrors the rail when >1 board exists. */
  processes?: ReadonlyArray<WorkProcessWire>;
  selectedProcessId?: string | null;
  onSelectProcess?: (processId: string) => void;
}

/** Encodes a process pick as an option value distinct from a view key. */
const PROCESS_OPT_PREFIX = 'process:';

/**
 * Mobile view-switcher: a native <select> built from the same groups as the
 * rail, with an <optgroup> per group. Below `md` the rail is hidden and this
 * compact control replaces it — native select = mobile-native picker,
 * accessible by default, far less code than a drawer. The "+ New process"
 * affordance is omitted here (it isn't a navigable view).
 */
export function WorkspaceNavSelect({ view, onSelect, className, processes, selectedProcessId, onSelectProcess }: WorkspaceNavSelectProps): ReactNode {
  const dynamicProcesses = processes && processes.length > 0 ? orderProcesses(processes) : null;
  // The current selection: a process pick when on the pipeline view, else the view.
  const value = dynamicProcesses && view === 'pipeline'
    ? `${PROCESS_OPT_PREFIX}${selectedProcessId ?? dynamicProcesses[0]!.id}`
    : view;
  return (
    <div className={className}>
      <label htmlFor="workspace-nav-select" className="sr-only">
        Workspace section
      </label>
      <SelectField size="default"
        id="workspace-nav-select"
        data-testid="workspace-nav-select"
        aria-label="Workspace section"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (v.startsWith(PROCESS_OPT_PREFIX)) onSelectProcess?.(v.slice(PROCESS_OPT_PREFIX.length));
          else onSelect(v as WorkspaceView);
        }}
        className="w-full"
      >
        {WORKSPACE_NAV_GROUPS.map((group) => {
          // Processes group with a live list → one option per board.
          const opts = group.label === 'Processes' && dynamicProcesses
            ? dynamicProcesses.map((proc) => (
                <option key={proc.id} value={`${PROCESS_OPT_PREFIX}${proc.id}`}>{proc.label}</option>
              ))
            : group.items.map((item) => (
                <option key={item.key} value={item.key}>{item.label}</option>
              ));
          // Headerless group (My Tasks) → bare options above the first optgroup.
          return group.label ? (
            <optgroup key={group.label} label={group.label}>
              {opts}
            </optgroup>
          ) : (
            <Fragment key={group.items[0]!.key}>{opts}</Fragment>
          );
        })}
      </SelectField>
    </div>
  );
}
